import { useCallback, useMemo, useState } from 'react';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useBeginArtistPortalAuthMutation } from '../../../redux/api/fymAPI';
import { ErrorAPIResult, isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { trackEvent } from '../../analytics/sml/analytics';
import { rememberSoundCloudConnect } from './soundCloudConnectSession';

export interface SoundCloudConnect {
  /** Starts the sign-in and leaves for SoundCloud. `returnTo`: the page to come back to. */
  connect: (returnTo?: string) => Promise<boolean>;
  /** True from the click until the browser leaves (design a2 state 2 "Connecting"). */
  isConnecting: boolean;
  error: ErrorAPIResult['error'] | undefined;
  reset: () => void;
}

/**
 * The SoundCloud sign-in (spec 2026-10-15 §2, design a2): `auth/begin` with an optional
 * `returnTo`, then the page leaves for the consent page that MusicAPI runs. MusicAPI sends the
 * browser back to `/app/catalog/connect/soundCloud?state=…` (the return page, design b).
 */
export function useSoundCloudConnect({ accountId }: { accountId: number }): SoundCloudConnect {
  const [begin] = useBeginArtistPortalAuthMutation();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<ErrorAPIResult['error']>();

  const connect = useCallback(
    async (returnTo?: string): Promise<boolean> => {
      setError(undefined);
      setIsConnecting(true);
      trackEvent('portal_connect_started', { service: MusicAPIIntegrationID.SoundCloud });
      const begun = await begin({ accountId, integrationType: MusicAPIIntegrationID.SoundCloud, returnTo });
      if (!isSuccessfulResult(begun)) {
        setIsConnecting(false);
        setError(begun.error);
        return false;
      }
      rememberSoundCloudConnect(begun.data.sessionUuid, accountId);
      window.location.assign(begun.data.authUrl);
      return true;
    },
    [accountId, begin]
  );
  const reset = useCallback(() => {
    setIsConnecting(false);
    setError(undefined);
  }, []);

  return useMemo(() => ({ connect, isConnecting, error, reset }), [connect, error, isConnecting, reset]);
}
